import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import Footer from "../common/Footer";
import Header from "../common/Header";
import ContactPgBackground from "../../Images/ContactUs-Background.webp";
import { Mail, Phone, MapPin } from "lucide-react";

const ContactPage = () => {
  const [buttonLabel, setButtonLabel] = useState("Send Message");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const formik = useFormik({
    initialValues: {
      name: "",
      phone: "",
      email: "",
      company_name: "",
      message: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      phone: Yup.string()
        .matches(
          /^\+\d[\d\s]{7,20}$/,
          "Phone must start with + and include digits (spaces allowed)"
        )
        .required("Phone number is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      company_name: Yup.string().required("Company name is required"),
      message: Yup.string().required("Message is required"),
    }),
    onSubmit: async (values, { resetForm, setFieldError }) => {
      setIsSubmitting(true);
      setButtonLabel("Sending...");

      try {
        const sanitizedValues = {
          ...values,
          phone: values.phone.replace(/\s+/g, ""), // remove spaces from phone
        };

        await axios.post(
          "https://backend.semarsholding.com/main-contact-form",
          sanitizedValues
        );
        resetForm();
        setButtonLabel("Message Sent!");
        setTimeout(() => setButtonLabel("Send Message"), 2500);
      } catch (error) {
        const msg = error?.response?.data?.message || "Submission failed";
        if (msg.toLowerCase().includes("phone")) {
          setFieldError("phone", msg);
        }
        setButtonLabel("Failed. Try Again");
        setTimeout(() => setButtonLabel("Send Message"), 3000);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <div>
      <Header />
      <div className="bg-black h-[5rem] md:h-[8rem]"></div>
      <section
        className="relative w-full min-h-screen text-white"
        style={{
          backgroundImage: `url(${ContactPgBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="flex flex-col items-center justify-center px-1 md:px-6 py-16 bg-black/70 min-h-screen">
          <div className="w-full max-w-8xl flex flex-col md:flex-row gap-8 md:gap-72 rounded-2xl p-2 md:p-8">
            {/* Left Section */}
            <div
              className="w-full md:w-1/2 space-y-6 md:space-y-12"
              data-aos="fade-right"
            >
              <h2 className="text-5xl font-bold text-gold-300">
              Let’s Discuss Your Next Move in Saudi Arabia
              </h2>
              <p className="text-lg text-gray-300 max-w-md">
                Fill up the form and our Team will get back to you within 24
                hours.
              </p>

              <div className="space-y-4">
                {[
                  {
                    icon: <MapPin className="w-5 h-5 text-[#C99C48]" />,
                    value: "Jeddah, Kingdom of Saudi Arabia",
                  },
                  // {
                  //   icon: <Phone className="w-5 h-5 text-[#C99C48]" />,
                  //   value: "+966 (Insert Number)",
                  // },
                  {
                    icon: <Mail className="w-5 h-5 text-[#C99C48]" />,
                    value: "info@semarsholding.com",
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-4 p-3 bg-transparent hover:bg-[#C99C4878] rounded-lg transition cursor-pointer max-w-md"
                  >
                    {item.icon}
                    <div>
                      <p className="font-semibold">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Section (Form) */}
            <div
              className="w-full md:w-1/2 bg-[#C99C4824] p-3 md:p-6 rounded-2xl"
              data-aos="fade-left"
            >
              <form onSubmit={formik.handleSubmit} className="space-y-4">
                {/* Name */}
                <div className="space-y-2">
                  <label className="text-[#C99C48] font-medium">Name</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter your name"
                    className={`w-full p-3 rounded-md bg-[#FFFFFF] text-[#333333] placeholder-[#333333] focus:outline-none focus:ring-2 focus:ring-[#C99C48] ${
                      formik.touched.name && formik.errors.name
                        ? "border-red-400"
                        : ""
                    }`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.name}
                  />
                  {formik.touched.name && formik.errors.name && (
                    <p className="text-red-400 text-sm">{formik.errors.name}</p>
                  )}
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <label className="text-[#C99C48] font-medium">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder={
                      formik.touched.phone && formik.errors.phone
                        ? formik.errors.phone
                        : "e.g. +966 512345678"
                    }
                    className={`w-full p-3 rounded-md bg-[#FFFFFF] text-[#333333] placeholder-[#333333] focus:outline-none focus:ring-2 focus:ring-[#C99C48] ${
                      formik.touched.phone && formik.errors.phone
                        ? "border-red-400"
                        : ""
                    }`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.phone}
                  />
                  {formik.touched.phone && formik.errors.phone && (
                    <p className="text-red-400 text-sm">
                      {formik.errors.phone}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label className="text-[#C99C48] font-medium">Email</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your Email Address"
                    className={`w-full p-3 rounded-md bg-[#FFFFFF] text-[#333333] placeholder-[#333333] focus:outline-none focus:ring-2 focus:ring-[#C99C48] ${
                      formik.touched.email && formik.errors.email
                        ? "border-red-400"
                        : ""
                    }`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                  />
                  {formik.touched.email && formik.errors.email && (
                    <p className="text-red-400 text-sm">
                      {formik.errors.email}
                    </p>
                  )}
                </div>

                {/* Company Name */}
                <div className="space-y-2">
                  <label className="text-[#C99C48] font-medium">
                    Company Name
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    placeholder="Enter your Company Name"
                    className={`w-full p-3 rounded-md bg-[#FFFFFF] text-[#333333] placeholder-[#333333] focus:outline-none focus:ring-2 focus:ring-[#C99C48] ${
                      formik.touched.company_name && formik.errors.company_name
                        ? "border-red-400"
                        : ""
                    }`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.company_name}
                  />
                  {formik.touched.company_name &&
                    formik.errors.company_name && (
                      <p className="text-red-400 text-sm">
                        {formik.errors.company_name}
                      </p>
                    )}
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <label className="text-[#C99C48] font-medium">Message</label>
                  <textarea
                    rows="4"
                    name="message"
                    placeholder="Write your message..."
                    className={`w-full p-3 rounded-md bg-[#FFFFFF] text-[#333333] placeholder-[#333333] focus:outline-none focus:ring-2 focus:ring-[#C99C48] ${
                      formik.touched.message && formik.errors.message
                        ? "border-red-400"
                        : ""
                    }`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.message}
                  />
                  {formik.touched.message && formik.errors.message && (
                    <p className="text-red-400 text-sm">
                      {formik.errors.message}
                    </p>
                  )}
                </div>

                {/* Submit */}
                <div className="text-center">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`bg-[#C99C48] hover:bg-[#B8914A] text-white font-medium py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 active:scale-95 ${
                      isSubmitting
                        ? "opacity-70 scale-95 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    {buttonLabel}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default ContactPage;
